import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  mapOfferCampaignError,
  useAuthSession,
  useDeleteOfferCampaign,
  useStoreOfferCampaigns,
  useTranslation,
  type OfferCampaignErrorKey,
  type OfferCampaignWithItems,
  type TranslationKey,
} from '@dukanti/shared';
import { AlertCircle, Info, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { SkeletonBar } from '@/features/products/components/skeleton-bar';
import { DeleteOfferCampaignDialog } from '@/features/offer-campaigns/components/delete-offer-campaign-dialog';
import { OfferCampaignsList } from '@/features/offer-campaigns/components/offer-campaigns-list';
import { MarketingTabs } from '@/features/promotional-ads/components/marketing-tabs';
import { usePromotionalAdsAccess } from '@/features/promotional-ads/hooks/use-promotional-ads-access';
import { useCurrentStore } from '@/hooks/use-current-store';
import { supabase } from '@/lib/supabase';

interface StoreContext {
  readonly storeId: string;
  readonly storeSlug: string;
}

function toStoreContext(storeId: string | null, storeSlug: string | null): StoreContext | null {
  if (!storeId || !storeSlug) return null;
  return { storeId, storeSlug };
}

export function OfferCampaignsRoute() {
  const { t, dir } = useTranslation();
  const navigate = useNavigate();
  // Epic #228 P7: the one Marketing gate of this route; the tab bar takes its state.
  const adsAccess = usePromotionalAdsAccess();
  const authSession = useAuthSession(supabase);
  const { storeId, storeSlug } = useCurrentStore(authSession);
  const storeContext = useMemo(
    () => toStoreContext(storeId, storeSlug ?? null),
    [storeId, storeSlug],
  );
  const campaignsQuery = useStoreOfferCampaigns(supabase, storeContext?.storeId ?? null);
  const deleteCampaign = useDeleteOfferCampaign(supabase);
  const [deletingCampaign, setDeletingCampaign] = useState<OfferCampaignWithItems | null>(null);
  const [deleteError, setDeleteError] = useState<OfferCampaignErrorKey | null>(null);

  function openCreate(): void {
    navigate('/promotions/campaigns/new');
  }

  function openEdit(campaign: OfferCampaignWithItems): void {
    navigate(`/promotions/campaigns/${campaign.id}/edit`);
  }

  function openDelete(campaign: OfferCampaignWithItems): void {
    setDeletingCampaign(campaign);
    setDeleteError(null);
  }

  function handleDeleteOpenChange(open: boolean): void {
    if (!open) {
      setDeletingCampaign(null);
      setDeleteError(null);
    }
  }

  function handleDeleteConfirm(): void {
    if (!deletingCampaign) return;
    deleteCampaign.mutate(
      { campaign_id: deletingCampaign.id },
      {
        onSuccess: () => {
          toast.success(t('admin.offerCampaigns.toasts.deleted' as TranslationKey));
          setDeletingCampaign(null);
          setDeleteError(null);
        },
        onError: (err: unknown) => {
          const mapped = mapOfferCampaignError(err);
          setDeleteError(mapped);
          toast.error(t(mapped as TranslationKey));
        },
      },
    );
  }

  const isError = campaignsQuery.isError || !storeContext;
  const errorKey = !storeContext ? 'common.errors.permissionDenied' : campaignsQuery.errorKey;

  return (
    <main
      dir={dir}
      data-testid="offer-campaigns-route"
      className="mx-auto w-full max-w-5xl px-4 py-6 sm:px-6 lg:px-8"
    >
      <div className="mb-6 flex flex-col justify-between gap-4 sm:flex-row sm:items-start">
        <div>
          <MarketingTabs active="campaigns" accessState={adsAccess.state} />
          <h1 className="font-display text-foreground text-start text-[28px] font-bold leading-tight tracking-tight">
            {t('admin.offerCampaigns.title' as TranslationKey)}
          </h1>
          <p className="text-muted-foreground mt-1 max-w-2xl text-start text-sm">
            {t('admin.offerCampaigns.subtitle' as TranslationKey)}
          </p>
        </div>
        <Button type="button" onClick={openCreate} disabled={!storeContext}>
          <Plus className="size-4" aria-hidden />
          <span>{t('admin.offerCampaigns.addNew' as TranslationKey)}</span>
        </Button>
      </div>

      <div
        role="note"
        data-testid="offer-campaigns-visibility-notice"
        className="border-saffron/40 bg-saffron/10 text-muted-foreground mb-5 flex items-start gap-2 rounded-lg border px-3 py-2 text-start text-sm"
      >
        <Info className="text-saffron mt-0.5 size-4 shrink-0" aria-hidden />
        <span>{t('admin.offerCampaigns.visibilityNotice' as TranslationKey)}</span>
      </div>

      {campaignsQuery.isLoading ? <OfferCampaignListSkeleton /> : null}

      {!campaignsQuery.isLoading && isError ? (
        <div
          role="alert"
          data-testid="offer-campaigns-route-error"
          className="border-border bg-card shadow-admin-card mx-auto max-w-lg rounded-lg border p-8 text-center"
        >
          <AlertCircle className="text-destructive mx-auto mb-4 size-10" strokeWidth={1.5} />
          <h2 className="font-display text-foreground mb-2 text-xl">{t('common.error')}</h2>
          <p className="text-muted-foreground mb-5 text-sm">
            {t((errorKey ?? 'common.errors.unknown') as TranslationKey)}
          </p>
          {storeContext ? (
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                void campaignsQuery.refetch();
              }}
            >
              {t('common.retry')}
            </Button>
          ) : null}
        </div>
      ) : null}

      {!campaignsQuery.isLoading && !isError && campaignsQuery.campaigns.length === 0 ? (
        <div className="border-border bg-card shadow-admin-card rounded-lg border p-8 text-center">
          <h2 className="font-display text-foreground mb-2 text-xl">
            {t('admin.offerCampaigns.emptyTitle' as TranslationKey)}
          </h2>
          <p className="text-muted-foreground mx-auto mb-5 max-w-md text-sm">
            {t('admin.offerCampaigns.emptyDescription' as TranslationKey)}
          </p>
          <Button type="button" onClick={openCreate}>
            <Plus className="size-4" aria-hidden />
            <span>{t('admin.offerCampaigns.addNew' as TranslationKey)}</span>
          </Button>
        </div>
      ) : null}

      {!campaignsQuery.isLoading && !isError && campaignsQuery.campaigns.length > 0 ? (
        <OfferCampaignsList
          campaigns={campaignsQuery.campaigns}
          storeSlug={storeContext?.storeSlug ?? ''}
          onEdit={openEdit}
          onDelete={openDelete}
          onLinkCopied={() =>
            toast.success(t('admin.offerCampaigns.toasts.linkCopied' as TranslationKey))
          }
        />
      ) : null}

      <DeleteOfferCampaignDialog
        campaignTitle={deletingCampaign?.title ?? null}
        open={deletingCampaign !== null}
        isDeleting={deleteCampaign.isPending}
        errorKey={deleteError}
        onOpenChange={handleDeleteOpenChange}
        onConfirm={handleDeleteConfirm}
      />
    </main>
  );
}

function OfferCampaignListSkeleton() {
  return (
    <div
      className="border-border bg-card shadow-admin-card rounded-lg border p-4"
      role="status"
      aria-busy="true"
    >
      <SkeletonBar className="mb-4 h-16 w-full rounded-lg" />
      <SkeletonBar className="mb-4 h-16 w-full rounded-lg" />
      <SkeletonBar className="h-16 w-full rounded-lg" />
    </div>
  );
}
